<script setup>
import { ModalsContainer } from 'vue-final-modal';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useCommonStore } from '~/common/stores/common.store';
import { accessTokenCookie, currentOrganizationCookie, purgeCommonStore } from '~/common/utils/common.utils';

const auth_store = useAuthStore();
const common_store = useCommonStore();
const services = inject('$services');

const is_loading = ref(true);

// Public link flow
// Fetches a JWT from the token endpoint first, then runs the exact same initial flow as the normal widget flow (set_logged_in_user_details, common_store.initialize) so token-refresh, /info, and all downstream initialisation work identically.
async function initializePublicFlow(report_id, organization, password) {
  try {
    // Step 1: fetch a JWT for the given report / organization.
    const { data } = await services.bi_dashboard.get_token({
      id: report_id,
      query: { organization },
      body: password ? { password } : {},
    });
    accessTokenCookie('set', data.token);

    // Step 2: run the normal flow with the JWT now in the cookie.
    purgeCommonStore();
    if (organization)
      currentOrganizationCookie('set', organization);

    await auth_store.set_logged_in_user_details({ forceUpdate: true });
    await common_store.initialize({ forceUpdate: true, toast: false });
  }
  catch (err) {
    logger.error('Failed during public dashboard initialization:', err);
  }
  finally {
    is_loading.value = false;
  }
}

async function initialize() {
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  // Public link: fetch a JWT, then run the normal flow
  if (params.public_token) {
    await initializePublicFlow(params.public_token, params.organization, params.password);
    return;
  }

  // Normal flow
  accessTokenCookie('set', params.token);

  if (!params.organization)
    return;

  purgeCommonStore();
  currentOrganizationCookie('set', params.organization);
  await auth_store.set_logged_in_user_details({ forceUpdate: true });
  await common_store.initialize({
    forceUpdate: true,
    toast: false,
  });
  is_loading.value = false;
}

onMounted(initialize);
</script>

<template>
  <div>
    <div v-if="is_loading" class="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
      <HawkLoader container_class="m-0 " />
    </div>
    <router-view
      v-else :options="{ can_modify_resource: false, hide_side_bar: true, is_stand_alone: true, hide_dashboard_options: true, show_top_bar: true }"
    />
    <ModalsContainer />
  </div>
</template>
